/**
 * Dénominations FCFA (Franc CFA)
 * Coupures disponibles en Côte d'Ivoire et dans la zone UEMOA
 */

export interface Denomination {
  readonly value: number;
  readonly label: string;
  readonly type: 'bill' | 'coin';
}

/**
 * Toutes les coupures disponibles, triées par ordre décroissant
 */
export const DENOMINATIONS: readonly Denomination[] = [
  { value: 10000, label: '10 000 FCFA', type: 'bill' },
  { value: 5000, label: '5 000 FCFA', type: 'bill' },
  { value: 2000, label: '2 000 FCFA', type: 'bill' },
  { value: 1000, label: '1 000 FCFA', type: 'bill' },
  { value: 500, label: '500 FCFA', type: 'bill' },
  { value: 200, label: '200 FCFA', type: 'coin' },
  { value: 100, label: '100 FCFA', type: 'coin' },
  { value: 50, label: '50 FCFA', type: 'coin' },
  { value: 25, label: '25 FCFA', type: 'coin' },
  { value: 10, label: '10 FCFA', type: 'coin' },
  { value: 5, label: '5 FCFA', type: 'coin' },
] as const;

/**
 * Billets uniquement (coupures papier)
 */
export const BILLS = DENOMINATIONS.filter(d => d.type === 'bill');

/**
 * Pièces uniquement (monnaie)
 */
export const COINS = DENOMINATIONS.filter(d => d.type === 'coin');

/**
 * Valeurs des dénominations (pour validation)
 */
export const DENOMINATION_VALUES = DENOMINATIONS.map(d => d.value);

/**
 * Dénomination minimale
 */
export const MIN_DENOMINATION = 5;

/**
 * Dénomination maximale
 */
export const MAX_DENOMINATION = 10000;

/**
 * Devise
 */
export const CURRENCY = 'XOF' as const;
export const CURRENCY_LABEL = 'Franc CFA' as const;
export const CURRENCY_SYMBOL = 'FCFA' as const;

/**
 * Vérifie si une valeur est une dénomination valide
 */
export function isValidDenomination(value: number): boolean {
  return DENOMINATION_VALUES.includes(value);
}

/**
 * Calcule le total d'un ensemble de billets/pièces
 */
export function calculateTotal(bills: Map<number, number>): number {
  let total = 0;
  bills.forEach((count, denomination) => {
    if (isValidDenomination(denomination)) {
      total += denomination * count;
    }
  });
  return total;
}

/**
 * Crée une map vide de dénominations (toutes à 0)
 */
export function createEmptyDenominationMap(): Map<number, number> {
  const map = new Map<number, number>();
  DENOMINATIONS.forEach(d => map.set(d.value, 0));
  return map;
}
